import deepmerge from 'deepmerge'

import { type StoryblokAkeneoCategoryPageType } from '../../../../storyblok'
import { type StoryblokProductSearchFiltersType } from '../StoryblokProductSearchFilters.types'

import { storyblokProductSearchFiltersGetCategories } from './storyblokProductSearchFiltersGetCategories'
import { storyblokProductSearchFiltersGetCategoriesOwnBrands } from './storyblokProductSearchFiltersGetCategoriesOwnBrands'
import { storyblokProductSearchFiltersGetDiscount } from './storyblokProductSearchFiltersGetDiscount'
import { storyblokProductSearchFiltersGetNovelties } from './storyblokProductSearchFiltersGetNovelties'
import { storyblokProductSearchFiltersGetNow } from './storyblokProductSearchFiltersGetNow'
import { storyblokProductSearchFiltersGetOffers } from './storyblokProductSearchFiltersGetOffers'
import { storyblokProductSearchFiltersGetPromotion } from './storyblokProductSearchFiltersGetPromotion'
import { storyblokProductSearchFiltersGetPromotionId } from './storyblokProductSearchFiltersGetPromotionId'
import { storyblokProductSearchFiltersGetRedpoints } from './storyblokProductSearchFiltersGetRedpoints'
import { storyblokProductSearchFiltersGetSets } from './storyblokProductSearchFiltersGetSets'

/**
 * Parses an array of Storyblok product search filter blocks into a consolidated
 * filter configuration object defined by StoryblokProductSearchFiltersType and understood by the app layer.
 *
 * Each supported blok (e.g. ProductSearchFilterCategories, ProductSearchFilterDiscount)
 * is transformed into its internal shape and progressively deep‑merged into the
 * accumulated result. Unsupported blok types are safely ignored.
 */
export const storyblokProductSearchFiltersParseBlocksToFilters = (
  productSearchFilters: StoryblokAkeneoCategoryPageType['productSearchFilters'] = [],
  defaultFilters: StoryblokProductSearchFiltersType = {},
  // eslint-disable-next-line max-statements -- storyblok blocks parsing
): StoryblokProductSearchFiltersType => {
  let filters: StoryblokProductSearchFiltersType = {}

  /* eslint-disable unicorn/no-break-in-nested-loop -- don't reference, refactor if touched */
  for (const storyblokFilterBlok of productSearchFilters) {
    switch (storyblokFilterBlok.component) {
      case 'ProductSearchFilterCategories': {
        filters = deepmerge<StoryblokProductSearchFiltersType>(
          filters,
          storyblokProductSearchFiltersGetCategories(storyblokFilterBlok),
        )
        break
      }
      case 'ProductSearchFilterDiscount': {
        filters = deepmerge<StoryblokProductSearchFiltersType>(
          filters,
          storyblokProductSearchFiltersGetDiscount(storyblokFilterBlok),
        )
        break
      }
      case 'ProductSearchFilterNovelties': {
        filters = deepmerge<StoryblokProductSearchFiltersType>(
          filters,
          storyblokProductSearchFiltersGetNovelties(storyblokFilterBlok),
        )
        break
      }
      case 'ProductSearchFilterNow': {
        filters = deepmerge<StoryblokProductSearchFiltersType>(
          filters,
          storyblokProductSearchFiltersGetNow(storyblokFilterBlok),
        )
        break
      }

      case 'ProductSearchFilterOffers': {
        filters = deepmerge<StoryblokProductSearchFiltersType>(
          filters,
          storyblokProductSearchFiltersGetOffers(storyblokFilterBlok),
        )

        break
      }
      case 'ProductSearchFilterOwnBrands': {
        filters = deepmerge<StoryblokProductSearchFiltersType>(
          filters,
          storyblokProductSearchFiltersGetCategoriesOwnBrands(
            storyblokFilterBlok,
          ),
        )
        break
      }

      case 'ProductSearchFilterPromotionId': {
        filters = deepmerge<StoryblokProductSearchFiltersType>(
          filters,
          storyblokProductSearchFiltersGetPromotionId(storyblokFilterBlok),
        )
        break
      }

      case 'ProductSearchFilterPromotions': {
        filters = deepmerge<StoryblokProductSearchFiltersType>(
          filters,
          storyblokProductSearchFiltersGetPromotion(),
        )
        break
      }

      case 'ProductSearchFilterRedpoints': {
        filters = deepmerge<StoryblokProductSearchFiltersType>(
          filters,
          storyblokProductSearchFiltersGetRedpoints(),
        )
        break
      }

      case 'ProductSearchFilterSets': {
        filters = deepmerge<StoryblokProductSearchFiltersType>(
          filters,
          storyblokProductSearchFiltersGetSets(),
        )
        break
      }

      default: {
        break
      }
    }
  }
  /* eslint-enable unicorn/no-break-in-nested-loop */

  return { ...defaultFilters, ...filters }
}
